/**
 * Puts each country's indicators next to the rest of the world.
 *
 * "77.5 years" is a fact you can read without learning anything. "77.5 years,
 * where the world median is 73.9" is a position, and a position is what you
 * remember. Everything here is derived from the same dataset the card already
 * shows — nothing new is fetched or stored.
 *
 * The bar is a percentile rather than a raw scale on purpose: GDP per capita
 * runs from $700 to $140,000 and is heavily skewed, so a linear bar would
 * leave four fifths of the world squeezed against the left edge. A percentile
 * spreads countries evenly and puts the median at the midpoint every time.
 */
import EnglishData from "./EnglishData.json";
import SpanishData from "./SpanishData.json";

const INDICATOR_BLOCK_KEYS = ["Indexes", "Índices"];

/** Keys inside the block that annotate it rather than measure anything. */
const META_KEY = /^(source|fuente)$/i;

/**
 * Reads the leading number out of a formatted indicator.
 *
 *   es: "32.587 USD (paridad de poder adquisitivo) (2025)" -> 32587
 *   en: "42.4 out of 100 — higher is more unequal (2024)"  -> 42.4
 *
 * The two files use opposite conventions for the decimal mark, so the
 * language has to be known; guessing from the string itself gets "8,0" and
 * "1,000" wrong in opposite directions.
 */
export const parseIndicatorValue = (
  raw: string,
  isSpanish: boolean
): number | undefined => {
  const match = isSpanish
    ? raw.match(/^-?[\d.]+(?:,\d+)?/)
    : raw.match(/^-?[\d,]+(?:\.\d+)?/);
  if (!match) return undefined;

  const normalized = isSpanish
    ? match[0].replace(/\./g, "").replace(",", ".")
    : match[0].replace(/,/g, "");
  const value = Number.parseFloat(normalized);
  return Number.isFinite(value) ? value : undefined;
};

/** Sorted values per indicator label, for one language's dataset. */
type Distribution = Map<string, number[]>;

const distributionCache = new Map<boolean, Distribution>();

const buildDistribution = (isSpanish: boolean): Distribution => {
  const dataset = (isSpanish ? SpanishData : EnglishData) as Record<
    string,
    Record<string, unknown>
  >;
  const byLabel: Distribution = new Map();

  Object.values(dataset).forEach((info) => {
    if (typeof info !== "object" || info === null) return;
    const block = INDICATOR_BLOCK_KEYS.map((key) => info[key]).find(
      (value) => typeof value === "object" && value !== null
    ) as Record<string, unknown> | undefined;
    if (!block) return;

    Object.entries(block).forEach(([label, raw]) => {
      if (typeof raw !== "string" || META_KEY.test(label.trim())) return;
      const value = parseIndicatorValue(raw, isSpanish);
      if (value === undefined) return;
      const bucket = byLabel.get(label);
      if (bucket) bucket.push(value);
      else byLabel.set(label, [value]);
    });
  });

  byLabel.forEach((values) => values.sort((a, b) => a - b));
  return byLabel;
};

const distributionFor = (isSpanish: boolean): Distribution => {
  const cached = distributionCache.get(isSpanish);
  if (cached) return cached;
  const built = buildDistribution(isSpanish);
  distributionCache.set(isSpanish, built);
  return built;
};

export interface IndicatorContext {
  value: number;
  median: number;
  /** How many countries carry this indicator at all. */
  count: number;
  /** Position on the 0-100 scale, ties split evenly. Drives the bar. */
  percentile: number;
  /** Share of countries strictly below this value. */
  shareBelow: number;
  /** Share of countries strictly above it. */
  shareAbove: number;
  /** How far from the middle, 0-50. Drives "the most unusual thing here". */
  distanceFromMedian: number;
}

export const indicatorContext = (
  label: string,
  raw: string,
  isSpanish: boolean
): IndicatorContext | undefined => {
  const values = distributionFor(isSpanish).get(label);
  // Below this the "median" is a word for a coincidence, not a middle.
  if (!values || values.length < 30) return undefined;

  const value = parseIndicatorValue(raw, isSpanish);
  if (value === undefined) return undefined;

  const middle = values.length / 2;
  const median =
    values.length % 2
      ? values[Math.floor(middle)]
      : (values[middle - 1] + values[middle]) / 2;

  // Ties matter here. The World Bank floors undernourishment at 2.5%, so
  // dozens of countries share that exact value: counting only what is
  // strictly below would put every one of them at percentile 0 and let the
  // card claim they are "lower than 100% of countries", which is false.
  // The bar splits ties down the middle, and the sentence only ever claims
  // what is strictly true.
  const below = values.filter((other) => other < value).length;
  const above = values.filter((other) => other > value).length;
  const ties = values.length - below - above;
  const percentile = ((below + ties / 2) / values.length) * 100;

  return {
    value,
    median,
    count: values.length,
    percentile,
    shareBelow: (below / values.length) * 100,
    shareAbove: (above / values.length) * 100,
    distanceFromMedian: Math.abs(percentile - 50),
  };
};

/**
 * The figure without its explanation: "92.4% live in cities (2025)" -> "92.4%".
 * The full phrase repeats what the label beside it already says, and three of
 * those stacked read as a paragraph instead of three numbers.
 */
export const shortIndicatorValue = (
  raw: string,
  isSpanish: boolean
): string => {
  const match = isSpanish
    ? raw.match(/^-?[\d.]+(?:,\d+)?%?/)
    : raw.match(/^-?[\d,]+(?:\.\d+)?%?/);
  return match ? match[0] : raw;
};

/** Locale-correct, and never more precision than the source had. */
export const formatIndicatorNumber = (
  value: number,
  isSpanish: boolean
): string =>
  value.toLocaleString(isSpanish ? "es-AR" : "en-US", {
    maximumFractionDigits: Math.abs(value) >= 1000 ? 0 : 1,
  });
